import React, { useMemo, useEffect, useRef, Component } from 'react';
import { ScheduleXCalendar, useCalendarApp } from '@schedule-x/react';
import {
  createViewWeek,
  createViewMonthGrid,
  createViewMonthAgenda,
  createViewDay,
} from '@schedule-x/calendar';
import { createDragAndDropPlugin } from '@schedule-x/drag-and-drop';
import { createEventsServicePlugin } from '@schedule-x/events-service';
import { createCurrentTimePlugin } from '@schedule-x/current-time';
import '@schedule-x/theme-default/dist/index.css';
import './ClubCalendar.css';

// Error boundary para capturar errores del calendario (p.ej. versión incompatible)
class CalendarErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error) {
    console.error('[ClubCalendar] Error:', error);
  }
  render() {
    if (this.state.hasError) {
      const msg = String(this.state.error?.message || '');
      const isTemporalError = msg.includes('Temporal');
      return (
        <div style={{ padding: 32, textAlign: 'center', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, color: '#991b1b' }}>
          <h3 style={{ margin: '0 0 12px' }}>Error al cargar el calendario</h3>
          {isTemporalError ? (
            <div style={{ textAlign: 'left', maxWidth: 700, margin: '0 auto' }}>
              <p style={{ marginBottom: 12 }}>
                <strong>Tienes instalada Schedule-X v4 que requiere la Temporal API.</strong>
              </p>
              <p style={{ marginBottom: 8 }}>Ejecuta en terminal (fuera de Windsurf):</p>
              <pre style={{ background: '#fff', padding: 12, borderRadius: 8, overflow: 'auto', fontSize: '0.75rem', lineHeight: 1.5 }}>
{`cd c:\\programs\\gymnastics-scores-app-newTemplate
npm install @schedule-x/calendar@2.29.0 @schedule-x/current-time@2.29.0 @schedule-x/drag-and-drop@2.29.0 @schedule-x/event-modal@2.29.0 @schedule-x/events-service@2.29.0 @schedule-x/react@2.11.0 @schedule-x/theme-default@2.29.0 --legacy-peer-deps`}
              </pre>
              <p style={{ marginTop: 12, fontSize: '0.85rem' }}>Luego reinicia con <code>npm start</code>.</p>
            </div>
          ) : (
            <p>{msg}</p>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Wrapper de Schedule-X para el calendario del club.
 *
 * Convierte `blocks` (recurrentes semanales con day_of_week 0-6) en
 * eventos concretos para ±WEEKS_SPAN semanas alrededor de hoy.
 *
 * Props:
 *  - blocks: Array<{ id, title, color, day_of_week, start_time, end_time, duration_minutes }>
 *  - onEventClick?: (block) => void
 *  - onEventDrop?: (block, changes) => void  // para persistir (futuro)
 *  - defaultView?: 'week' | 'month-grid' | 'month-agenda' | 'day'
 */
const WEEKS_SPAN = 6; // ±6 semanas (12 semanas visibles)

const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtDateTime = (d) => `${fmtDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = domingo
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
};

const hexToCalendarColors = (hex) => {
  // Convierte hex a paleta Schedule-X { colorName, lightColors, darkColors }
  // IMPORTANTE: colorName debe ser un identificador CSS válido (sin '#'),
  // porque Schedule-X lo usa para generar clases CSS.
  const safeHex = hex && /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#8b5cf6';
  const colorName = 'c' + safeHex.slice(1).toLowerCase(); // ej. "c8b5cf6"
  return {
    colorName,
    lightColors: {
      main: safeHex,
      container: safeHex + '33',
      onContainer: safeHex,
    },
    darkColors: {
      main: safeHex,
      onContainer: '#ffffff',
      container: safeHex + '66',
    },
  };
};

// Obtiene los días [0-6] del bloque. Prioriza days_of_week (array) con fallback a day_of_week (int)
const getBlockDaysOfWeek = (block) => {
  if (Array.isArray(block.days_of_week) && block.days_of_week.length > 0) {
    return block.days_of_week.map(Number).filter(n => !isNaN(n) && n >= 0 && n <= 6);
  }
  if (block.day_of_week != null) return [Number(block.day_of_week)];
  return [];
};

const generateEventsFromBlocks = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length === 0) return { events: [], calendars: {} };

  const today = new Date();
  const baseSunday = startOfWeek(today);
  baseSunday.setDate(baseSunday.getDate() - WEEKS_SPAN * 7);

  const events = [];
  const calendars = {};

  for (let w = 0; w < WEEKS_SPAN * 2; w++) {
    blocks.forEach((block) => {
      if (!block.start_time) return;
      const dows = getBlockDaysOfWeek(block);
      if (dows.length === 0) return;

      dows.forEach((dow) => {
        const start = new Date(baseSunday);
        start.setDate(start.getDate() + w * 7 + dow);

        const [sh, sm] = (block.start_time || '00:00').split(':').map(Number);
        start.setHours(sh || 0, sm || 0, 0, 0);

        const end = new Date(start);
        if (block.end_time) {
          const [eh, em] = block.end_time.split(':').map(Number);
          end.setHours(eh || 0, em || 0, 0, 0);
          if (end <= start) {
            end.setTime(start.getTime() + (block.duration_minutes || 60) * 60000);
          }
        } else {
          end.setTime(start.getTime() + (block.duration_minutes || 60) * 60000);
        }

        const calId = `cal-${block.id}`;
        if (!calendars[calId]) {
          calendars[calId] = hexToCalendarColors(block.color);
        }

        events.push({
          id: `${block.id}-w${w}-d${dow}`,
          _blockId: block.id,
          title: block.title || 'Bloque',
          start: fmtDateTime(start),
          end: fmtDateTime(end),
          calendarId: calId,
          description: `${block.duration_minutes || 60} min`,
        });
      });
    });
  }

  return { events, calendars };
};

// Componente interno que se remonta cuando cambia `calendarKey` (para refrescar eventos)
const ClubCalendarInner = ({
  blocks,
  onEventClick,
  onEventDrop,
  defaultView,
}) => {
  const { events, calendars } = useMemo(() => generateEventsFromBlocks(blocks), [blocks]);

  const eventsServicePlugin = useMemo(() => createEventsServicePlugin(), []);
  const dragAndDropPlugin = useMemo(() => createDragAndDropPlugin(), []);
  const currentTimePlugin = useMemo(() => createCurrentTimePlugin(), []);

  // Usamos refs para que los callbacks siempre apunten a la versión más reciente de blocks/handlers
  const blocksRef = useRef(blocks);
  const onEventClickRef = useRef(onEventClick);
  const onEventDropRef = useRef(onEventDrop);
  useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  useEffect(() => { onEventClickRef.current = onEventClick; }, [onEventClick]);
  useEffect(() => { onEventDropRef.current = onEventDrop; }, [onEventDrop]);

  const calendar = useCalendarApp({
    views: [
      createViewWeek(),
      createViewMonthGrid(),
      createViewMonthAgenda(),
      createViewDay(),
    ],
    defaultView,
    events,
    calendars,
    locale: 'es-ES',
    firstDayOfWeek: 1, // Lunes
    dayBoundaries: { start: '06:00', end: '22:00' },
    weekOptions: {
      gridHeight: 640,
      nDays: 7,
      eventWidth: 95,
      timeAxisFormatOptions: { hour: '2-digit', minute: '2-digit' },
    },
    plugins: [eventsServicePlugin, dragAndDropPlugin, currentTimePlugin],
    callbacks: {
      onEventClick(ev) {
        const block = blocksRef.current.find((b) => String(b.id) === String(ev._blockId));
        if (block && onEventClickRef.current) onEventClickRef.current(block);
      },
      onEventUpdate(updated) {
        const block = blocksRef.current.find((b) => String(b.id) === String(updated._blockId));
        if (!block || !onEventDropRef.current) return;
        const s = new Date(updated.start.replace(' ', 'T'));
        const e = new Date(updated.end.replace(' ', 'T'));
        const newDow = s.getDay();

        // Si el bloque tiene múltiples días, extraer el día original del event id
        // (formato: "{blockId}-w{w}-d{dow}")
        const match = /-d(\d)$/.exec(updated.id || '');
        const originalDow = match ? Number(match[1]) : null;
        const currentDays = getBlockDaysOfWeek(block);

        let newDays;
        if (currentDays.length <= 1) {
          newDays = [newDow];
        } else if (originalDow != null && currentDays.includes(originalDow)) {
          // Reemplazar el día arrastrado por el nuevo
          newDays = currentDays.map((d) => (d === originalDow ? newDow : d));
          // Eliminar duplicados y ordenar
          newDays = Array.from(new Set(newDays)).sort((a, b) => a - b);
        } else {
          newDays = [newDow];
        }

        const changes = {
          days_of_week: newDays,
          day_of_week: newDays[0], // compat
          start_time: `${pad(s.getHours())}:${pad(s.getMinutes())}:00`,
          end_time: `${pad(e.getHours())}:${pad(e.getMinutes())}:00`,
          duration_minutes: Math.round((e - s) / 60000),
        };
        onEventDropRef.current(block, changes);
      },
    },
  });

  return (
    <div className="club-calendar-wrapper">
      <ScheduleXCalendar calendarApp={calendar} />
    </div>
  );
};

// Wrapper público: remonta el calendario cuando cambian los bloques
// para que Schedule-X re-lea los eventos correctamente.
const ClubCalendar = ({
  blocks = [],
  onEventClick,
  onEventDrop,
  defaultView = 'week',
}) => {
  // Key basada en IDs + horarios de los bloques: cambia solo cuando realmente cambian
  const calendarKey = useMemo(() => {
    if (!Array.isArray(blocks) || blocks.length === 0) return 'empty';
    return blocks
      .map((b) => `${b.id}:${getBlockDaysOfWeek(b).join(',')}:${b.start_time}:${b.end_time}:${b.color}:${b.title}`)
      .join('|');
  }, [blocks]);

  return (
    <CalendarErrorBoundary>
      <ClubCalendarInner
        key={calendarKey}
        blocks={blocks}
        onEventClick={onEventClick}
        onEventDrop={onEventDrop}
        defaultView={defaultView}
      />
    </CalendarErrorBoundary>
  );
};

export default ClubCalendar;
